import React from "react";
import Modal from "@cloudscape-design/components/modal";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import Checkbox from "@cloudscape-design/components/checkbox";
import Container from "@cloudscape-design/components/container";
import Alert from "@cloudscape-design/components/alert";
import Table from "@cloudscape-design/components/table";
import Tabs from "@cloudscape-design/components/tabs";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { prizeAddModalStore } from "../stores/prizeAddModal";
import Header from "@cloudscape-design/components/header";
import Grid from "@cloudscape-design/components/grid";
import DatePicker from "@cloudscape-design/components/date-picker";
import { gameStore } from "../stores/games";
import { RISK_MODEL_OPTIONS, getRiskModelSelectedOptions } from "../helpers/riskModelOptions";
import { PARTNER_PAYMENT_TYPE_OPTIONS, hasValidItemUidCondition } from "../helpers/cohortConditions";
import { formatChestPrizeType, getChestBannerUrl } from "../helpers/chestPrizeTypes";

const parseChestRewardConditions = (value, fallback = {}) => {
	if (Array.isArray(value)) {
		return value;
	}

	if (value && typeof value === "object") {
		return value;
	}

	if (typeof value === "string" && value.trim().length) {
		try {
			return JSON.parse(value);
		} catch (error) {
			return fallback;
		}
	}

	return fallback;
};

const getChestRewardsPayload = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions, []);

	if (Array.isArray(parsed)) {
		return parsed;
	}

	if (Array.isArray(parsed?.prizes)) {
		return parsed.prizes;
	}

	return [];
};

const buildChestPrizeOption = (item) => ({
	value: String(item.id),
	label: `${item.name} (${item.id})`,
	description: formatChestPrizeType(item.type),
	chestPrize: item,
});

const FREESPINS_PROVIDER_OPTIONS = [{ label: "Endorphina", value: "endorphina" }];

class PrizeAddModal extends React.Component {
	state = {
		imageError: "",
		levelError: "",
		cdnUrl: "",
		cohortGroups: [
			{
				groupId: crypto.randomUUID(),
				module: "deposit",
				groupKey: "depositFilters",
				groupLogicalOperator: null,
			},
		],
		newConditionByGroupId: {},
	};

	componentDidMount() {
		this.loadCdnConfig();
	}

	componentDidUpdate(prevProps) {
		const prevRewardType = prevProps.prizeAddModal.rewardType?.value;
		const currentRewardType = this.props.prizeAddModal.rewardType?.value;

		if (prevRewardType !== "chest" && currentRewardType === "chest" && !this.props.prizeAddModal.chestPrizeOptions.length) {
			this.loadChestPrizeOptions();
		}
	}

	loadCdnConfig = async () => {
		try {
			const response = await this.props.Interface.request(
				"get",
				{ class: "data", module: "personalPromotions", method: "getConfig" },
				{}
			);

			if (response?.cdn) {
				this.setState({ cdnUrl: response.cdn });
			}
		} catch (error) {
			console.error("Failed to load CDN config", error);
		}
	};

	getGroupKeyByModule = (module) => {
		if (module === "deposit") return "depositFilters";
		if (module === "withdrawal") return "withdrawalFilters";
		if (module === "registration") return "registrationFilters";
		return "profileFilters";
	};

	getCohortModuleOptions = () => [
		{ value: "deposit", label: "Deposit" },
		{ value: "withdrawal", label: "Withdrawal" },
		{ value: "registration", label: "Registration" },
		{ value: "profile", label: "Profile" },
	];

	getLogicalOperatorOptions = () => [
		{ value: "AND", label: "AND" },
		{ value: "OR", label: "OR" },
	];

	getCohortFieldOptions = () => [
		{ value: "id", label: "Id", type: "string" },
		{ value: "risk_model", label: "Risk model", type: "multiselect", specialType: "riskModel" },
		{ value: "verify", label: "Verify", type: "select", specialType: "boolean" },
		{ value: "sweepstakes_count", label: "Sweepstakes_count", type: "count" },
		{ value: "email", label: "Email", type: "select", specialType: "boolean" },
		{ value: "phone", label: "Phone", type: "select", specialType: "boolean" },
		{ value: "tg", label: "Telegram", type: "select", specialType: "boolean" },
		{ value: "partnerName", label: "Partner Name", type: "string" },
		{ value: "paymentType", label: "Payment Type", type: "select", specialType: "partnerPaymentType" },
		{ value: "trafficType", label: "Traffic Type", type: "string" },

		{ value: "deposit_date", label: "Deposit date", type: "count" },
		{ value: "deposit_date_crypto", label: "Deposit crypto date", type: "count" },
		{ value: "deposit_date_fiat", label: "Deposit fiat date", type: "count" },
		{ value: "deposit_count", label: "Deposit count", type: "count" },
		{ value: "deposit_count_fiat", label: "Deposit fiat count", type: "count" },
		{ value: "deposit_count_crypto", label: "Deposit crypto count", type: "count" },
		{ value: "deposit_from", label: "Deposit from", type: "date" },
		{ value: "deposit_to", label: "Deposit to", type: "date" },
		{ value: "deposit_crypto_from", label: "Deposit crypto from", type: "date" },
		{ value: "deposit_crypto_to", label: "Deposit crypto to", type: "date" },
		{ value: "deposit_fiat_from", label: "Deposit fiat from", type: "date" },
		{ value: "deposit_fiat_to", label: "Deposit fiat to", type: "date" },
		{ value: "deposit_amount_to", label: "Deposit amount to", type: "count" },
		{ value: "deposit_amount_from", label: "Deposit amount from", type: "count" },
		{ value: "deposit_amount_crypto_to", label: "Deposit amount crypto to", type: "count" },
		{ value: "deposit_amount_crypto_from", label: "Deposit amount crypto from", type: "count" },
		{ value: "deposit_amount_fiat_to", label: "Deposit amount fiat to", type: "count" },
		{ value: "deposit_amount_fiat_from", label: "Deposit amount fiat from", type: "count" },
		{ value: "deposit_type", label: "Deposit type", type: "select", specialType: "cryptoOrFiat" },

		{ value: "withdrawal_date", label: "Withdrawal date", type: "count" },
		{ value: "withdrawal_date_crypto", label: "Withdrawal crypto date", type: "count" },
		{ value: "withdrawal_date_fiat", label: "Withdrawal fiat date", type: "count" },
		{ value: "withdrawal_count", label: "Withdrawal count", type: "count" },
		{ value: "withdrawal_count_fiat", label: "Withdrawal fiat count", type: "count" },
		{ value: "withdrawal_count_crypto", label: "Withdrawal crypto count", type: "count" },
		{ value: "withdrawal_from", label: "Withdrawal from", type: "date" },
		{ value: "withdrawal_to", label: "Withdrawal to", type: "date" },
		{ value: "withdrawal_crypto_from", label: "Withdrawal crypto from", type: "date" },
		{ value: "withdrawal_crypto_to", label: "Withdrawal crypto to", type: "date" },
		{ value: "withdrawal_fiat_from", label: "Withdrawal fiat from", type: "date" },
		{ value: "withdrawal_fiat_to", label: "Withdrawal fiat to", type: "date" },
		{ value: "withdrawal_amount_to", label: "Withdrawal amount to", type: "count" },
		{ value: "withdrawal_amount_from", label: "Withdrawal amount from", type: "count" },
		{ value: "withdrawal_amount_crypto_to", label: "Withdrawal amount crypto to", type: "count" },
		{ value: "withdrawal_amount_crypto_from", label: "Withdrawal amount crypto from", type: "count" },
		{ value: "withdrawal_amount_fiat_to", label: "Withdrawal amount fiat to", type: "count" },
		{ value: "withdrawal_amount_fiat_from", label: "Withdrawal amount fiat from", type: "count" },
		{ value: "withdrawal_type", label: "Withdrawal type", type: "select", specialType: "cryptoOrFiat" },

		{ value: "spins", label: "Spins", type: "count" },
		{ value: "lvl_club", label: "Club", type: "count" },
		{ value: "referer", label: "Referer", type: "count" },
		{ value: "balance", label: "Balance", type: "count" },
		{ value: "registration_from", label: "Registration from", type: "date" },
		{ value: "registration_to", label: "Registration to", type: "date" },
		{ value: "registration_date", label: "Registration user days", type: "count" },
	];

	getCohortFieldOptionsByModule = (module) => {
		const fields = this.getCohortFieldOptions();

		if (module === "deposit") return fields.filter((f) => f.value.startsWith("deposit_"));
		if (module === "withdrawal") return fields.filter((f) => f.value.startsWith("withdrawal_"));
		if (module === "registration") return fields.filter((f) => f.value.startsWith("registration_"));

		return fields.filter(
			(f) =>
				!f.value.startsWith("deposit_") &&
				!f.value.startsWith("withdrawal_") &&
				!f.value.startsWith("registration_")
		);
	};

	getCohortStateKeys = () => ({
		conditionsKey: "conditions",
		groupsKey: "cohortGroups",
		newConditionKey: "newConditionByGroupId",
	});

	updateNewCondition = (groupId, patch, cohortType = "visibility") => {
		const { newConditionKey } = this.getCohortStateKeys(cohortType);

		this.setState((prev) => ({
			[newConditionKey]: {
				...(prev[newConditionKey] || {}),
				[groupId]: {
					...(prev[newConditionKey]?.[groupId] || {}),
					...patch,
				},
			},
		}));
	};

	resetNewCondition = (groupId, cohortType = "visibility") => {
		const { newConditionKey } = this.getCohortStateKeys(cohortType);

		this.setState((prev) => ({
			[newConditionKey]: {
				...(prev[newConditionKey] || {}),
				[groupId]: {},
			},
		}));
	};

	isConditionValueEmpty = (value) => value === null || value === "" || (Array.isArray(value) && value.length === 0);

	normalizeGroupConditions = (conditions, groupId) => {
		const groupConditions = (conditions || [])
			.filter((c) => c.groupId === groupId)
			.sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));

		if (!groupConditions.length) return conditions;

		const firstId = groupConditions[0].id;
		return (conditions || []).map((c) => {
			if (c.groupId !== groupId) return c;
			if (c.id === firstId) return { ...c, logicalOperator: null };
			if (!c.logicalOperator) return { ...c, logicalOperator: "AND" };
			return c;
		});
	};

	addCohortGroup = (cohortType = "visibility") => {
		const groupId = crypto.randomUUID();
		const { groupsKey } = this.getCohortStateKeys(cohortType);

		this.setState((prev) => {
			const groups = [...(prev[groupsKey] || [])];
			const groupLogicalOperator = groups.length === 0 ? null : "AND";

			return {
				[groupsKey]: [
					...groups,
					{
						groupId,
						module: "deposit",
						groupKey: "depositFilters",
						groupLogicalOperator,
					},
				],
			};
		});
	};

	updateCohortGroup = (groupId, patch, cohortType = "visibility") => {
		const { conditionsKey, groupsKey } = this.getCohortStateKeys(cohortType);
		const nextGroups = (this.state[groupsKey] || []).map((g) =>
			g.groupId === groupId ? { ...g, ...patch } : g
		);

		this.setState({ [groupsKey]: nextGroups });

		const conditions = this.props.prizeAddModal[conditionsKey] || [];
		const updatedConditions = conditions.map((c) =>
			c.groupId === groupId
				? {
						...c,
						...(typeof patch.module !== "undefined" ? { module: patch.module } : {}),
						...(typeof patch.module !== "undefined"
							? { groupKey: this.getGroupKeyByModule(patch.module) }
							: {}),
						...(typeof patch.groupLogicalOperator !== "undefined"
							? { groupLogicalOperator: patch.groupLogicalOperator }
							: {}),
				  }
				: c
		);

		this.props.setPrizeAddModal({
			...this.props.prizeAddModal,
			[conditionsKey]: updatedConditions,
		});
	};

	changeCohortGroupModule = (groupId, module, cohortType = "visibility") => {
		const { conditionsKey, groupsKey } = this.getCohortStateKeys(cohortType);
		const nextGroups = (this.state[groupsKey] || []).map((g) =>
			g.groupId === groupId ? { ...g, module, groupKey: this.getGroupKeyByModule(module) } : g
		);

		this.setState({ [groupsKey]: nextGroups });

		const nextConditions = (this.props.prizeAddModal[conditionsKey] || []).filter(
			(c) => c.groupId !== groupId
		);

		this.props.setPrizeAddModal({
			...this.props.prizeAddModal,
			[conditionsKey]: nextConditions,
		});

		this.resetNewCondition(groupId, cohortType);
	};

	removeCohortGroup = (groupId, cohortType = "visibility") => {
		const { conditionsKey, groupsKey, newConditionKey } = this.getCohortStateKeys(cohortType);
		const nextGroups = (this.state[groupsKey] || [])
			.filter((g) => g.groupId !== groupId)
			.map((g, idx) => ({
				...g,
				groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
			}));

		const groupIndexById = new Map(nextGroups.map((g, idx) => [g.groupId, idx]));

		const nextConditions = (this.props.prizeAddModal[conditionsKey] || [])
			.filter((c) => c.groupId !== groupId)
			.filter((c) => groupIndexById.has(c.groupId))
			.map((c) => {
				const groupOrder = groupIndexById.get(c.groupId);
				const group = nextGroups[groupOrder];

				return {
					...c,
					module: group.module,
					groupOrder,
					groupLogicalOperator: groupOrder === 0 ? null : group.groupLogicalOperator || "AND",
				};
		});

		this.setState((prev) => {
			const nextNewConditionByGroupId = { ...(prev[newConditionKey] || {}) };
			delete nextNewConditionByGroupId[groupId];

			return {
				[groupsKey]: nextGroups,
				[newConditionKey]: nextNewConditionByGroupId,
			};
		});

		this.props.setPrizeAddModal({
			...this.props.prizeAddModal,
			[conditionsKey]: nextConditions,
		});
	};

	addCohortCondition = (group, groupIndex, cohortType = "visibility") => {
		const { conditionsKey, newConditionKey } = this.getCohortStateKeys(cohortType);
		const conditions = this.props.prizeAddModal[conditionsKey] || [];
		const groupConditions = conditions
			.filter((c) => c.groupId === group.groupId)
			.sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));

		const newCondition = this.state[newConditionKey]?.[group.groupId] || {};
		if (!newCondition.field || this.isConditionValueEmpty(newCondition.value)) return;

		let value = newCondition.value;
		if (newCondition.conditionalType === "select") {
			value = newCondition.value.value || newCondition.value;
		} else if (newCondition.conditionalType === "boolean") {
			value = newCondition.value ? 1 : 0;
		}

		const nextOrder = groupConditions.length
			? Math.max(...groupConditions.map((c) => c.conditionOrder ?? 0)) + 1
			: 0;

		const logicalOperator = groupConditions.length
			? newCondition.logicalOperator || "AND"
			: null;

		const item = {
			...newCondition,
			id: crypto.randomUUID(),
			value,
			logicalOperator,
			conditionOrder: nextOrder,
			groupId: group.groupId,
			module: group.module,
			groupKey: group.groupKey || this.getGroupKeyByModule(group.module),
			groupOrder: groupIndex,
			groupLogicalOperator: groupIndex === 0 ? null : group.groupLogicalOperator || "AND",
		};

		const nextConditions = this.normalizeGroupConditions(
			[...conditions, item],
			group.groupId
		);

		this.props.setPrizeAddModal({
			...this.props.prizeAddModal,
			[conditionsKey]: nextConditions,
		});
		this.resetNewCondition(group.groupId, cohortType);
	};



	componentWillUnmount() {
		if (this.props.prizeAddModal.image) {
			URL.revokeObjectURL(this.props.prizeAddModal.image);
		}
	}
	
	onDismiss = () => {
		this.props.setPrizeAddModal({
			...this.props.prizeAddModal,
			name: "",
			description: "",
			category_id: null,
			rewardValue: "",
			image: null,
			cycle: null,
			cycleUnit: null,
			cycleTimer: null,
			buttonText: "",
			link: "",
			active: "",
			categories: [],
			conditions: [],
			count: "",
			nameTranslationType: "",
			descriptionTranslationType: "",
			order: null,
			fakeWinsPerMonth: "",
			chestRewards: [],
			chestType: "",
			chestPrizeId: null,
			chestPrizeSelected: null,
			chestPrizeOptions: [],
			chestPrizeLoading: false,
			chestPrizeMatchMissing: false,
			hidden: false,
			showInBanner: false,
			loaded: true,
			show: false,
		});
	}

	loadAvailableGames(provider) {
		if (provider === 'endorphina') {
			const formattedGames = this.getAvailableEndorphinaGames();

			if (!formattedGames.length) {
				this.props.Interface.addNoty("Error", "Please reload page game state not loading");
				return;
			}

			this.props.setPrizeAddModal(prevState => ({
				...prevState,
				availableGames: formattedGames
			}));
		}
	}

	getAvailableEndorphinaGames = () => {
		const gameState = this.props.game;
		if (!gameState?.loaded || !gameState?.data) {
			return [];
		}

		return gameState.data
			.filter((game) => game.providerID === 10 && game.active === 1)
			.map((game) => ({
				id: game.gameID,
				description: game.name,
				name: game.name,
				gameID: game.gameID,
				providerID: game.providerID,
			}));
	};

	getFilteredGames() {
		const games = this.props.prizeAddModal.availableGames || [];

		return games.map(game => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game
		}));
	}

	getChestRewardGames = () =>
		this.getAvailableEndorphinaGames().map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game,
		}));

	buildFreespinRewardConditions = ({ gameSelected, betAmount, expiresAt }) => {
		const parsedBetAmount = parseFloat(betAmount) || 0;

		return {
			gameId: gameSelected?.gameId,
			providerId: gameSelected?.game?.providerID,
			betAmount: parsedBetAmount,
			currency: "RUB",
			expiresAt: parseInt(expiresAt),
		};
	};

	buildChestRewardsPayload = () => getChestRewardsPayload(this.props.prizeAddModal.chestRewards || []);

	loadChestPrizeOptions = async () => {
		try {
			this.props.setPrizeAddModal((prevState) => ({
				...prevState,
				chestPrizeLoading: true,
			}));

			const response = await this.props.Interface.getChestPrizeOptions({ page: 1, limit: 999 });
			const options = (response?.items || []).map((item) => buildChestPrizeOption(item));

			this.props.setPrizeAddModal((prevState) => ({
				...prevState,
				chestPrizeOptions: options,
				chestPrizeLoading: false,
			}));
		} catch (error) {
			console.error(error);
			this.props.setPrizeAddModal((prevState) => ({
				...prevState,
				chestPrizeLoading: false,
			}));
			this.props.Interface.addNoty("Error", "Failed to load chest presets");
		}
	};

	selectChestPrize = (selectedOption) => {
		const chestPrize = selectedOption?.chestPrize || null;

		this.props.setPrizeAddModal({
			...this.props.prizeAddModal,
			chestPrizeSelected: selectedOption,
			chestPrizeId: chestPrize?.id || null,
			chestType: chestPrize?.type || "",
			chestRewards: getChestRewardsPayload(chestPrize?.rewardConditions),
			chestPrizeMatchMissing: false,
		});
	};

	handleIconChange = (event) => {
		const file = event.target.files[0];
		this.setState({ imageError: "" });

		if (file && (file.type === "image/svg+xml" || file.type === "image/png")) {
			if (this.props.prizeAddModal.icon) {
				URL.revokeObjectURL(this.props.prizeAddModal.icon);
			}
			const imageUrl = URL.createObjectURL(file);
			this.props.setPrizeAddModal({
				...this.props.prizeAddModal,
				icon: imageUrl,
				iconFile: file,
			});
		} else {
			this.setState({ imageError: "Please select a valid SVG" });
			this.props.setPrizeAddModal({
				...this.props.prizeAddModal,
				icon: "",
				iconFile: null,
			});
		}
	};



	handleImageChange = (event) => {
		const file = event.target.files[0];
		this.setState({ imageError: "" });

		if (file && (file.type === "image/png")) {
			if (this.props.prizeAddModal.image) {
				URL.revokeObjectURL(this.props.prizeAddModal.image);
			}
			const imageUrl = URL.createObjectURL(file);
			this.props.setPrizeAddModal({
				...this.props.prizeAddModal,
				image: imageUrl,
				imageFile: file,
			});
		} else {
			this.setState({ imageError: "Please select a valid PNG file" });
			this.props.setPrizeAddModal({
				...this.props.prizeAddModal,
				image: "",
				imageFile: null,
			});
		}
	};

	fileToBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
			reader.readAsDataURL(file);
		});
	};

	validation() {
		try {
			if (this.props.prizeAddModal.rewardType?.value === "freespins") {
					if (!this.props.prizeAddModal.providerSelected) {
						return "Provider for freespins is required";
					}
					if (!this.props.prizeAddModal.gameSelected) {
						return "Game for freespins is required";
					}
					if (!this.props.prizeAddModal.betAmount) {
						return "Bet amount for freespins is required";
					}
					if (!this.props.prizeAddModal.expiresAt) {
						return "ExpiresAt for freespins is required";
					}

					const spinsCount = parseInt(this.props.prizeAddModal.rewardValue) || 0;
					if (!this.props.prizeAddModal.rewardValue || spinsCount < 1 || spinsCount > 1000) {
						return "Please enter rewardValue count (1-1000)";
					}

					const betAmount = parseFloat(this.props.prizeAddModal.betAmount) || 0;
					if (betAmount <= 0) {
						return "Please enter bet amount";
					}
				} else if (this.props.prizeAddModal.rewardType?.value === "chest") {
					if (!this.props.prizeAddModal.chestPrizeId) {
						return "Chest preset is required";
					}

				} else if (!this.props.prizeAddModal.rewardType?.value) {
					return "Reward type is required";
			}

			const hasCohortCondition = (this.props.prizeAddModal.conditions || []).some(
				(condition) =>
					condition?.field &&
					condition?.value !== null &&
					condition?.value !== undefined &&
					condition?.value !== ""
			);

			if (this.props.prizeAddModal.hidden && !hasCohortCondition) {
				return "Hidden prize requires cohort settings";
			}

			if (
				this.props.prizeAddModal.rewardType?.value === "item" &&
				(this.props.prizeAddModal.conditions || []).length > 0 &&
				!hasValidItemUidCondition(this.props.prizeAddModal.conditions)
			) {
				return "Item prizes require a Profile / Id condition using = or IN.";
			}

			return "";
		}catch (e) {
			console.log("TVAR", e);
		}
	}

	isSubmitDisabled = () => {
		const { prizeAddModal } = this.props;
		const requiresRewardValue = prizeAddModal.rewardType?.value !== "chest";
		const requiresChestPreset = prizeAddModal.rewardType?.value === "chest";
		const requiresImage = true;
		const requiresItemUidCondition =
			prizeAddModal.rewardType?.value === "item" &&
			(prizeAddModal.conditions || []).length > 0;

		return (
			!prizeAddModal.name ||
			!prizeAddModal.description ||
			!prizeAddModal.rewardType ||
			!prizeAddModal.poolCount ||
			!prizeAddModal.poolType ||
			!prizeAddModal.active ||
			(requiresImage && !prizeAddModal.image) ||
			(requiresRewardValue && !prizeAddModal.rewardValue) ||
			(requiresChestPreset && !prizeAddModal.chestPrizeId) ||
			(requiresItemUidCondition && !hasValidItemUidCondition(prizeAddModal.conditions)) ||
			Number(prizeAddModal.probability) < 0
		);
	};

	addPrize = async () => {
		try {
			this.props.setPrizeAddModal({
				...this.props.prizeAddModal,
				loaded: false,
			});

			let imageBase64 = "";
			let imageExtension = "";

			let iconBase64 = "";
			let iconExtension = "";

			if (this.props.prizeAddModal.imageFile) {
				try {
					imageBase64 = await this.fileToBase64(
						this.props.prizeAddModal.imageFile
					);
					imageExtension =
						this.props.prizeAddModal.imageFile.type === "image/png"
							? "png"
							: "svg";
				} catch (error) {
					console.error("Failed to convert image to base64:", error);
					this.props.Interface.addNoty("Error", "Failed to process image");
					this.props.setPrizeAddModal({
						...this.props.prizeAddModal,
						loaded: true,
					});
					return;
				}
			}

			if (this.props.prizeAddModal.iconFile) {
				try {
					iconBase64 = await this.fileToBase64(
						this.props.prizeAddModal.iconFile
					);
					iconExtension =
						this.props.prizeAddModal.iconFile.type === "image/png"
							? "png"
							: "svg";
				} catch (error) {
					console.error("Failed to convert image to base64:", error);
					this.props.Interface.addNoty("Error", "Failed to process image");
					this.props.setPrizeAddModal({
						...this.props.prizeAddModal,
						loaded: true,
					});
					return;
				}
			}


			const buildRewardConditions = () => {
				const type = this.props.prizeAddModal.rewardType?.value;
				const hidden = Boolean(this.props.prizeAddModal.hidden);
				const showInBanner = Boolean(this.props.prizeAddModal.showInBanner);

				if (type === "chest") {
					return {
						chestPrizeId: Number(this.props.prizeAddModal.chestPrizeId),
						hidden,
						showInBanner,
					};
				}

				if (type === "freespins") {
					return {
						...this.buildFreespinRewardConditions({
							gameSelected: this.props.prizeAddModal.gameSelected,
							betAmount: this.props.prizeAddModal.betAmount,
							expiresAt: this.props.prizeAddModal.expiresAt,
						}),
						hidden,
						showInBanner,
					};
				}

				return { hidden, showInBanner };
			}


			const validation = this.validation();

			if (validation?.length) {
				this.props.Interface.addNoty("Error", validation);
				this.props.setPrizeAddModal({
					...this.props.prizeAddModal,
					loaded: true,
				});
				return;
			}

			const fakeWinsPerMonthValue = parseInt(this.props.prizeAddModal.fakeWinsPerMonth, 10);
			const fakeWinsPerMonth = Number.isNaN(fakeWinsPerMonthValue) ? null : fakeWinsPerMonthValue;
			const isChestPrize = this.props.prizeAddModal.rewardType?.value === "chest";

			const payload = {
				name: this.props.prizeAddModal.name,
				rewardType: this.props.prizeAddModal.rewardType.value,
				type: this.props.prizeAddModal.rewardType?.value === "chest" ? this.props.prizeAddModal.chestType : undefined,
				description: this.props.prizeAddModal.description,
				poolType: this.props.prizeAddModal.poolType.value,
				poolCount: parseInt(this.props.prizeAddModal.poolCount),
				rewardValue: this.props.prizeAddModal.rewardType?.value === "chest" ? 0 : parseInt(this.props.prizeAddModal.rewardValue),
				image: imageBase64,
				icon: isChestPrize ? "" : iconBase64,
				iconExtension: isChestPrize ? "" : iconExtension,
				imageExtension,
				cohort: this.props.prizeAddModal.conditions,
				nameTranslationType: this.props.prizeAddModal.nameTranslationType,
				descriptionTranslationType: this.props.prizeAddModal.descriptionTranslationType,
				probability: this.props.prizeAddModal.probability,
				active: parseInt(this.props.prizeAddModal.active.value),
				rewardConditions: buildRewardConditions(),
				showInBanner: this.props.prizeAddModal.showInBanner ? 1 : 0,
				order: this.props.prizeAddModal.order,
				fakeWinsPerMonth,
			};

			this.props.Interface.request(
				"set",
				{ class: "data", module: "prize", method: "create" },
				payload
			)
				.then(() => {
					this.props.Interface.addNoty(
						"Success",
						`Prize "${this.props.prizeAddModal.name}" was successfully added`
					);

					if (this.props.prizeAddModal.image) {
						URL.revokeObjectURL(this.props.prizeAddModal.image);
					}

					this.props.setPrizeAddModal({
						...this.props.prizeAddModal,
						show: false,
						loaded: true,
						name: "",
						description: "",
						poolType: "",
						poolCount: "",
						rewardType: "",
						rewardValue: "",
						providerSelected: null,
						availableGames: [],
						conditions: [],
						gameSelected: null,
						betAmount: "",
						chestRewards: [],
						chestType: "",
						chestPrizeId: null,
						chestPrizeSelected: null,
						chestPrizeOptions: [],
						chestPrizeLoading: false,
						chestPrizeMatchMissing: false,
						icon: null,
						imageFile: null,
						iconFile: null,
						expiresAt: "",
						hidden: false,
						showInBanner: false,
						image: null,
						probability: "",
						fakeWinsPerMonth: "",
						link: "",
						active: "",
					});
					this.setState({ imageError: "" });

					this.props.Interface.loadPrizeList();
				})
				.catch((error) => {
					console.error("Failed to add prize:", error);
					this.props.setPrizeAddModal({
						...this.props.prizeAddModal,
						loaded: true,
					});
					this.props.Interface.addNoty("Error", "Failed to create prize");
				});
		} catch (e) {
			console.log(e);
			this.props.Interface.addNoty("Error", e.text);
			this.props.setPrizeAddModal({
				...this.props.prizeAddModal,
				loaded: true,
			});
		}
	};
	renderComponentFromType(groupId, type, specialType, cohortType = "visibility") {
		//count boolean date string

		const { newConditionKey } = this.getCohortStateKeys(cohortType);
		const newCondition = this.state[newConditionKey]?.[groupId] || {};

		if (type === "multiselect" && specialType === "riskModel") {
			return <Multiselect
				selectedOptions={getRiskModelSelectedOptions(newCondition?.value)}
				onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOptions.map((option) => option.value) }, cohortType)}
				options={RISK_MODEL_OPTIONS}
				placeholder="Select risk models"
				selectedAriaLabel="Selected"
			/>;
		} else if (type === "string") {
			return <Input
				placeholder="Enter value"
				value={newCondition?.value || ""}
				onChange={(e) => this.updateNewCondition(groupId, { value: e.detail.value }, cohortType)}
			/>
		} else if (type === "count") {
			return <Input
				placeholder="Enter value"
				type="number"
				value={newCondition?.value || ""}
				onChange={(e) => this.updateNewCondition(groupId, { value: e.detail.value }, cohortType)}
			/>
		} else if (type === "date") {
			return <DatePicker
				onChange={({ detail }) => {
					this.updateNewCondition(groupId, { value: detail.value }, cohortType);
				}}
				value={newCondition?.value || null}
				placeholder="Select a date"
			/>
		} else if (type === "select" && specialType === "boolean") {
			return <Select
				selectedOption={
					newCondition?.value
				}
				onChange={({ detail }) =>
					this.updateNewCondition(groupId, { value: detail.selectedOption }, cohortType)
				}
				options={[{ value: "1", label: "Required" }, { value: "0", label: "No" }]}
				placeholder="Select type"
			/>
		} else if (type === "select" && specialType === "cryptoOrFiat") {
			return <Select
				selectedOption={
					newCondition?.value
				}
				onChange={({ detail }) =>
					this.updateNewCondition(groupId, { value: detail.selectedOption }, cohortType)
				}
				options={[{ value: "crypto", label: "Crypto" }, { value: "fiat", label: "Fiat" }]}
				placeholder="Select type"
			/>
		} else if (type === "select" && specialType === "partnerPaymentType") {
			return <Select
				selectedOption={
					newCondition?.value
				}
				onChange={({ detail }) =>
					this.updateNewCondition(groupId, { value: detail.selectedOption }, cohortType)
				}
				options={PARTNER_PAYMENT_TYPE_OPTIONS}
				placeholder="Select payment type"
			/>
		}

		return null;
	}

	renderCohortEditor = ({ cohortType = "visibility", description = "" } = {}) => {
		const { conditionsKey, groupsKey, newConditionKey } = this.getCohortStateKeys(cohortType);
		const groups = this.state[groupsKey] || [];
		const conditions = this.props.prizeAddModal[conditionsKey] || [];

		return (
			<SpaceBetween size="l">
				{description && <Alert type="info">{description}</Alert>}
				<Box>
					<Button
						variant="primary"
						iconName="add-plus"
						onClick={() => this.addCohortGroup(cohortType)}
					>
						Add group
					</Button>
				</Box>
				{groups.map((group, groupIndex) => {
					const moduleOptions = this.getCohortModuleOptions();
					const logicalOperatorOptions = this.getLogicalOperatorOptions();
					const selectedModule = moduleOptions.find((o) => o.value === group.module) || null;
					const groupOperatorValue = groupIndex === 0 ? "AND" : group.groupLogicalOperator || "AND";
					const selectedGroupLogicalOperator = logicalOperatorOptions.find((o) => o.value === groupOperatorValue) || null;
					const groupConditions = conditions
						.filter((c) => c.groupId === group.groupId)
						.sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));
					const newCondition = this.state[newConditionKey]?.[group.groupId] || {};
					const selectedConditionLogicalOperator = logicalOperatorOptions.find((o) => o.value === (newCondition.logicalOperator || "AND")) || null;
					const fieldOptions = this.getCohortFieldOptionsByModule(group.module);
					const selectedFieldOption = newCondition.field ? { value: newCondition.field, label: newCondition.fieldLabel } : null;
					const selectedOperatorOption = newCondition.operator ? { value: newCondition.operator, label: newCondition.operator } : null;
					const isAddDisabled =
						!newCondition.field ||
						this.isConditionValueEmpty(newCondition.value) ||
						(!newCondition.operator && newCondition.conditionalType !== "select" && newCondition.conditionalType !== "multiselect");

					return (
						<Container
							key={group.groupId}
							header={
								<Header
									variant="h3"
									actions={
										<Button
											variant="icon"
											iconName="delete-marker"
											onClick={() => this.removeCohortGroup(group.groupId, cohortType)}
										>
											Remove group
										</Button>
									}
								>
									{`Group ${groupIndex + 1}`}
								</Header>
							}
						>
							<SpaceBetween size="m">
								<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
									<FormField label="Module" stretch>
										<Select
											selectedOption={selectedModule}
											onChange={({ detail }) =>
												this.changeCohortGroupModule(group.groupId, detail.selectedOption.value, cohortType)
											}
											options={moduleOptions}
											placeholder="Select module"
										/>
									</FormField>

									<FormField label="Group operator" stretch>
										<Select
											selectedOption={selectedGroupLogicalOperator}
											onChange={({ detail }) => {
												if (groupIndex === 0) return;
												this.updateCohortGroup(group.groupId, { groupLogicalOperator: detail.selectedOption.value }, cohortType);
											}}
											options={logicalOperatorOptions}
											placeholder="Select operator"
											disabled={groupIndex === 0}
										/>
									</FormField>

									<Box />
								</Grid>

								<Table
									columnDefinitions={[
										{ id: "logic", header: "Logic", cell: (item) => item.logicalOperator || "-" },
										{ id: "field", header: "Field", cell: (item) => item.fieldLabel || item.field },
										{ id: "operator", header: "Operator", cell: (item) => item.operator },
										{ id: "value", header: "Value", cell: (item) => Array.isArray(item.value) ? item.value.join(", ") : item.value },
										{
											id: "actions",
											header: "Control",
											cell: (item) => (
												<Button
													variant="icon"
													iconName="delete-marker"
													onClick={() => {
														const next = this.normalizeGroupConditions(
															conditions.filter((c) => c.id !== item.id),
															group.groupId
														);
														this.props.setPrizeAddModal({
															...this.props.prizeAddModal,
															[conditionsKey]: next,
														});
													}}
												>
													Remove
												</Button>
											),
										},
									]}
									footer={
										<FormField stretch label="Add condition">
											<Grid
												gridDefinition={
													groupConditions.length
														? [{ colspan: 2 }, { colspan: 4 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }]
														: [{ colspan: 4 }, { colspan: 3 }, { colspan: 3 }, { colspan: 2 }]
												}
											>
												{groupConditions.length > 0 && (
													<Select
														selectedOption={selectedConditionLogicalOperator}
														placeholder="AND/OR"
														options={logicalOperatorOptions}
														onChange={({ detail }) =>
															this.updateNewCondition(group.groupId, { logicalOperator: detail.selectedOption.value }, cohortType)
														}
													/>
												)}

												<Select
													selectedOption={selectedFieldOption}
													placeholder="Select field"
													options={fieldOptions}
													onChange={({ detail }) =>
														this.updateNewCondition(group.groupId, {
															field: detail.selectedOption.value,
															fieldLabel: detail.selectedOption.label,
															conditionalType: detail.selectedOption.type,
															specialType: detail.selectedOption.specialType,
															value: null,
															operator: detail.selectedOption.type === "select" || detail.selectedOption.type === "multiselect" ? "=" : null,
														}, cohortType)
													}
												/>

												<Select
													selectedOption={selectedOperatorOption}
													placeholder="Select operator"
													disabled={newCondition.conditionalType === "select" || newCondition.conditionalType === "multiselect"}
													options={[
														{ value: "=", label: "=" },
														{ value: ">", label: ">" },
														{ value: "<", label: "<" },
														{ value: ">=", label: ">=" },
														{ value: "<=", label: "<=" },
														{ value: "IN", label: "IN" },
													]}
													onChange={({ detail }) =>
														this.updateNewCondition(group.groupId, { operator: detail.selectedOption.value }, cohortType)
													}
												/>

												<Box>
													{this.renderComponentFromType(
														group.groupId,
														newCondition.conditionalType,
														newCondition.specialType,
														cohortType
													)}
												</Box>

												<Button
													disabled={isAddDisabled}
													onClick={() => this.addCohortCondition(group, groupIndex, cohortType)}
												>
													+ Add
												</Button>
											</Grid>
										</FormField>
									}
									items={groupConditions}
									empty="No conditions set"
								/>
							</SpaceBetween>
						</Container>
					);
				})}
			</SpaceBetween>
		);
	};



	render() {
		const isChestPrize = this.props.prizeAddModal.rewardType?.value === "chest";
		const chestBannerUrl = getChestBannerUrl(this.state.cdnUrl, this.props.prizeAddModal.chestType);
		const bannerPreviewUrl = this.props.prizeAddModal.image || (isChestPrize ? chestBannerUrl : "");

		return (
			<Modal
				visible={this.props.prizeAddModal.show}
				onDismiss={() => {
					if (this.props.prizeAddModal.image) {
						URL.revokeObjectURL(this.props.prizeAddModal.image);
					}
					this.onDismiss();
					this.setState({ imageError: "", levelError: "" });
				}}
				header="Add Prize"
				size="large"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button
								variant="link"
								onClick={() => {
									this.onDismiss();
								}}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								iconName="check"
								disabled={this.isSubmitDisabled()}
								onClick={this.addPrize}
								loading={!this.props.prizeAddModal.loaded}
							>
								Create Prize
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				{this.props.prizeAddModal.loaded ? (
					<Form>
						{this.state.imageError && (
							<Alert
								type="error"
								dismissible
								onDismiss={() => this.setState({ imageError: "" })}
							>
								{this.state.imageError}
							</Alert>
						)}
						<Tabs tabs={[{
							id: "general",
							label: "General",
							content: <SpaceBetween direction="vertical" size="l">
								<Container header={<Box variant="h3">General</Box>}>
									<SpaceBetween direction="vertical" size="m">
										<FormField label="Prize Name" stretch>
											<Input
												placeholder="Enter prize name"
												value={this.props.prizeAddModal.name}
												onChange={(e) =>
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														name: e.detail.value,
													})
												}
											/>
										</FormField>

										<FormField label="Name translation type" stretch>
											<Select
												selectedOption={this.props.prizeAddModal.nameTranslationType ? { value: this.props.prizeAddModal.nameTranslationType, label: this.props.prizeAddModal.nameTranslationType.charAt(0).toUpperCase() + this.props.prizeAddModal.nameTranslationType.slice(1) } : null}
												onChange={(e) => {
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														nameTranslationType: e.detail.selectedOption.value,
													})
												}}
												options={[
													{ value: "local", label: "Local" },
													{ value: "international", label: "International" },
													{ value: "mixed", label: "Mixed" },
												]}
												placeholder="Select prize name translation type"
											/>
										</FormField>

										<FormField label="Description" stretch>
											<Input
												placeholder="Enter description"
												value={this.props.prizeAddModal.description}
												onChange={(e) =>
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														description: e.detail.value,
													})
												}
											/>
										</FormField>

										<FormField label="Description translation type" stretch>
											<Select
												selectedOption={this.props.prizeAddModal.descriptionTranslationType ? { value: this.props.prizeAddModal.descriptionTranslationType, label: this.props.prizeAddModal.descriptionTranslationType.charAt(0).toUpperCase() + this.props.prizeAddModal.descriptionTranslationType.slice(1) } : null}
												onChange={(e) => {
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														descriptionTranslationType: e.detail.selectedOption.value,
													})
												}}
												options={[
													{ value: "local", label: "Local" },
													{ value: "international", label: "International" },
													{ value: "mixed", label: "Mixed" },
												]}
												placeholder="Select prize descritpion translation type"
											/>
										</FormField>

										<FormField label="Reward type" stretch>
											<Select
												selectedOption={this.props.prizeAddModal.rewardType}
												onChange={({ detail }) => {
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														rewardType: detail.selectedOption,
														probability: this.props.prizeAddModal.probability,
														image: detail.selectedOption.value !== "chest" ? null : this.props.prizeAddModal.image,
														imageFile: detail.selectedOption.value !== "chest" ? null : this.props.prizeAddModal.imageFile,
														gameSelected: null,
														availableGames: [],
														providerSelected: null,
														rewardValue: "",
														betAmount: "",
														expiresAt: "",
														chestRewards: [],
														chestType: "",
														chestPrizeId: null,
														chestPrizeSelected: null,
														chestPrizeOptions: detail.selectedOption.value === "chest" ? this.props.prizeAddModal.chestPrizeOptions : [],
														chestPrizeLoading: false,
														chestPrizeMatchMissing: false,
													});

													if (detail.selectedOption.value === "chest") {
														this.loadChestPrizeOptions();
													}
												}}
												options={this.props.prizeAddModal.currentOption.type}
												placeholder="Select a reward type"
											/>
										</FormField>
										{this.props.prizeAddModal.rewardType?.value !== "chest" && (
											<FormField label="Reward value" stretch>
												<Input
													type="number"
													placeholder="Enter reward value"
													value={this.props.prizeAddModal.rewardValue}
													onChange={(e) =>
														this.props.setPrizeAddModal({
															...this.props.prizeAddModal,
															rewardValue: e.detail.value,
														})
													}
												/>
											</FormField>
										)}
										<FormField label="Probability" stretch>
											<Input
												type="number"
												placeholder="Enter probability value"
												value={this.props.prizeAddModal.probability}
												onChange={(e) =>
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														probability: e.detail.value,
													})
												}
											/>
										</FormField>
										<FormField label="Order" stretch>
											<Input
												type="number"
												placeholder="Enter order value"
												value={this.props.prizeAddModal.order}
												onChange={(e) =>
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														order: e.detail.value,
													})
												}
											/>
										</FormField>
										<FormField label="Fake wins per month" stretch>
											<Input
												type="number"
												placeholder="Enter fake wins per month"
												value={this.props.prizeAddModal.fakeWinsPerMonth ?? ""}
												onChange={(e) =>
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														fakeWinsPerMonth: e.detail.value,
													})
												}
											/>
										</FormField>
										<FormField stretch>
											<Checkbox
												checked={Boolean(this.props.prizeAddModal.hidden)}
												onChange={({ detail }) =>
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														hidden: detail.checked,
													})
												}
											>
												Hidden prize
											</Checkbox>
										</FormField>
										<FormField stretch>
											<Checkbox
												checked={Boolean(this.props.prizeAddModal.showInBanner)}
												onChange={({ detail }) =>
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														showInBanner: detail.checked,
													})
												}
											>
												Show in banner
											</Checkbox>
										</FormField>

									</SpaceBetween>
								</Container>

								{this.props.prizeAddModal.rewardType?.value === "freespins" && <Container header={<Box variant="h3">Parameters</Box>}>
									<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 },  { colspan: 6 },  { colspan: 6 },   { colspan: 6 }, { colspan: 6 }, { colspan: 6 },  { colspan: 6 }, { colspan: 12 },]}>
										<FormField label="Provider">
											<Select
												filteringType="auto"
												placeholder="Select Provider"
												selectedOption={this.props.prizeAddModal.providerSelected}
												onChange={(option) => {
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														providerSelected: option.detail.selectedOption,
														gameSelected: null,
														availableGames: [],
													});

													this.loadAvailableGames(option.detail.selectedOption.value);
												}}
												options={FREESPINS_PROVIDER_OPTIONS}
											/>
										</FormField>

										{this.props.prizeAddModal.providerSelected && (
											<>
												<FormField label="Game">
													<Select
														filteringType="auto"
														placeholder="Select Game (type to search...)"
														selectedOption={this.props.prizeAddModal.gameSelected}
														onChange={(option) => {
															this.props.setPrizeAddModal({
																...this.props.prizeAddModal,
																gameSelected: option.detail.selectedOption
															});
														}}
														options={this.getFilteredGames()}
													/>
												</FormField>

												<FormField label="Bet Amount">
													<Input
														type="number"
														inputMode="decimal"
														placeholder="Enter bet amount (e.g., 100 for 100.00 RUB)"
														value={this.props.prizeAddModal.betAmount || ""}
														onChange={(input) => {
															this.props.setPrizeAddModal({
																...this.props.prizeAddModal,
																betAmount: input.detail.value
															});
														}}
													/>
												</FormField>

												<FormField label="Expiration Date">
													<Input
														placeholder="Enter expiration date"
														value={this.props.prizeAddModal.expiresAt || ""}
														onChange={(input) => {
															this.props.setPrizeAddModal({
																...this.props.prizeAddModal,
																expiresAt: input.detail.value
															});
														}}
													/>
												</FormField>
											</>

										)}
									</Grid>
								</Container>}

								{this.props.prizeAddModal.rewardType?.value === "chest" && (
									<Container header={<Header variant="h3">Chest preset</Header>}>
										<SpaceBetween direction="vertical" size="m">
											<FormField label="Chest" stretch>
												<Select
													filteringType="auto"
													placeholder="Select chest preset"
													selectedOption={this.props.prizeAddModal.chestPrizeSelected}
													onChange={({ detail }) => this.selectChestPrize(detail.selectedOption)}
													options={this.props.prizeAddModal.chestPrizeOptions}
													statusType={this.props.prizeAddModal.chestPrizeLoading ? "loading" : "finished"}
													loadingText="Loading chest presets"
													empty="No chest presets found"
												/>
											</FormField>

											{this.props.prizeAddModal.chestPrizeMatchMissing && (
												<Alert type="warning">
													Saved chest config does not match any preset. Select a chest to replace it.
												</Alert>
											)}

											{this.props.prizeAddModal.chestType && (
												<Box>{`Chest type: ${formatChestPrizeType(this.props.prizeAddModal.chestType)}`}</Box>
											)}

											{(this.props.prizeAddModal.chestRewards || []).length > 0 && (
												<Box>{`Rewards in chest: ${(this.props.prizeAddModal.chestRewards || []).length}`}</Box>
											)}
										</SpaceBetween>
									</Container>
								)}


								<Container header={<Box variant="h3">Pool settings</Box>}>
									<Grid gridDefinition={[{ colspan: 12 }, { colspan: 12 }]}>
										<FormField label="Type" stretch>
											<Select
												selectedOption={
													this.props.prizeAddModal.poolType
												}
												onChange={({ detail }) =>
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														poolType: detail.selectedOption,
													})
												}
												options={this.props.prizeAddModal.currentOption.poolType}
												placeholder="Select pool type"
											/>
										</FormField>
										<FormField label="Count" stretch>
											<Input
												type="number"
												placeholder="Enter count"
												value={this.props.prizeAddModal.poolCount}
												onChange={(e) =>
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														poolCount: e.detail.value,
													})
												}
											/>
										</FormField>
									</Grid>
								</Container>


								<Container header={<Box variant="h3">Other</Box>}>
									<Grid gridDefinition={isChestPrize ? [{ colspan: 12 }, { colspan: 12 }] : [{ colspan: 12 }, { colspan: 6 }, { colspan: 6 }]}>
										<FormField label="Active" stretch>
											<Select
												selectedOption={
													this.props.prizeAddModal.active
												}
												onChange={({ detail }) =>
													this.props.setPrizeAddModal({
														...this.props.prizeAddModal,
														active: detail.selectedOption,
													})
												}
												options={[{ value: "1", label: "Enabled" }, { value: "0", label: "Disabled" }]}
												placeholder="Select active"
											/>
										</FormField>
										<>
											<FormField
												label="Banner image"
												description={isChestPrize ? "Upload PNG image or use default chest banner" : "Upload PNG image"}
												stretch
											>
												<input
													type="file"
													accept="image/png"
													onChange={this.handleImageChange}
												/>
												{bannerPreviewUrl && (
													<Box margin={{ top: "s" }} textAlign="center">
														<img
															src={bannerPreviewUrl}
															alt={isChestPrize && !this.props.prizeAddModal.image ? "chest banner preview" : "prize preview"}
															style={{
																maxWidth: "100%",
																maxHeight: "150px",
																border: "1px solid #d5dbdb",
																borderRadius: "4px",
																padding: "4px",
																backgroundColor: "#fafafa",
															}}
														/>
													</Box>
												)}
											</FormField>

											{!isChestPrize && (
												<FormField
													label="Icon"
													description="Upload an SVG or PNG image"
													stretch
												>
													<input
														type="file"
														accept="image/svg+xml,image/png"
														onChange={this.handleIconChange}
													/>
													{this.props.prizeAddModal.icon && (
														<Box margin={{ top: "s" }} textAlign="center">
															<img
																src={this.props.prizeAddModal.icon}
																alt="icon preview"
																style={{
																	maxWidth: "100%",
																	maxHeight: "150px",
																	border: "1px solid #d5dbdb",
																	borderRadius: "4px",
																	padding: "4px",
																	backgroundColor: "#fafafa",
																}}
															/>
														</Box>
													)}
												</FormField>
											)}
										</>
									</Grid>
								</Container>

							</SpaceBetween>,
						},
						{
								id: "cohort",
								label: "Cohort Settings",
								content: (
									<SpaceBetween size="l">
										{this.props.prizeAddModal.rewardType?.value === "item" && (
											<Alert type="info">
												Without Cohort Settings, this item is display-only and cannot be won. If you add conditions, Item prizes require a Profile / Id condition using = or IN.
											</Alert>
										)}
										<Box>
											<Button
												variant="primary"
												iconName="add-plus"
												onClick={this.addCohortGroup}
											>
												Add group
											</Button>
										</Box>
										{(this.state.cohortGroups || []).map((group, groupIndex) => {
											const moduleOptions = this.getCohortModuleOptions();
											const logicalOperatorOptions = this.getLogicalOperatorOptions();

											const selectedModule =
												moduleOptions.find((o) => o.value === group.module) || null;

											const groupOperatorValue =
												groupIndex === 0 ? "AND" : group.groupLogicalOperator || "AND";

											const selectedGroupLogicalOperator =
												logicalOperatorOptions.find((o) => o.value === groupOperatorValue) ||
												null;

											const groupConditions = (this.props.prizeAddModal.conditions || [])
												.filter((c) => c.groupId === group.groupId)
												.sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));

											const newCondition =
												this.state.newConditionByGroupId?.[group.groupId] || {};

											const selectedConditionLogicalOperator =
												logicalOperatorOptions.find(
													(o) => o.value === (newCondition.logicalOperator || "AND")
												) || null;

											const fieldOptions = this.getCohortFieldOptionsByModule(group.module);

											const selectedFieldOption = newCondition.field
												? { value: newCondition.field, label: newCondition.fieldLabel }
												: null;

											const selectedOperatorOption = newCondition.operator
												? { value: newCondition.operator, label: newCondition.operator }
												: null;

											const isAddDisabled =
												!newCondition.field ||
												this.isConditionValueEmpty(newCondition.value) ||
												(!newCondition.operator && newCondition.conditionalType !== "select" && newCondition.conditionalType !== "multiselect");

											return (
												<Container
													key={group.groupId}
													header={
														<Header
															variant="h3"
															actions={
																<Button
																	variant="icon"
																	iconName="delete-marker"
																	onClick={() => this.removeCohortGroup(group.groupId)}
																>
																	Remove group
																</Button>
															}
														>
															{`Group ${groupIndex + 1}`}
														</Header>
													}
												>
									<SpaceBetween size="m">
														<Grid
															gridDefinition={[
																{ colspan: 4 },
																{ colspan: 4 },
																{ colspan: 4 },
															]}
														>
															<FormField label="Module" stretch>
																<Select
																	selectedOption={selectedModule}
																	onChange={({ detail }) =>
																		this.changeCohortGroupModule(
																			group.groupId,
																			detail.selectedOption.value
																		)
																	}
																	options={moduleOptions}
																	placeholder="Select module"
																/>
															</FormField>

															<FormField label="Group operator" stretch>
																<Select
																	selectedOption={selectedGroupLogicalOperator}
																	onChange={({ detail }) => {
																		if (groupIndex === 0) return;
																		this.updateCohortGroup(group.groupId, {
																			groupLogicalOperator: detail.selectedOption.value,
																		});
																	}}
																	options={logicalOperatorOptions}
																	placeholder="Select operator"
																	disabled={groupIndex === 0}
																/>
															</FormField>

															<Box />
														</Grid>

											<Table
												columnDefinitions={[
																{
																	id: "logic",
																	header: "Logic",
																	cell: (item) => item.logicalOperator || "-",
																},
																{
																	id: "field",
																	header: "Field",
																	cell: (item) => item.fieldLabel || item.field,
																},
																{
																	id: "operator",
																	header: "Operator",
																	cell: (item) => item.operator,
																},
																{
																	id: "value",
																	header: "Value",
																	cell: (item) => Array.isArray(item.value) ? item.value.join(", ") : item.value,
																},
													{
														id: "actions",
														header: "Control",
																	cell: (item) => (
															<Button
																variant="icon"
																iconName="delete-marker"
																onClick={() => {
																				const next = this.normalizeGroupConditions(
																					(this.props.prizeAddModal.conditions || []).filter(
																						(c) => c.id !== item.id
																					),
																					group.groupId
																				);
																	this.props.setPrizeAddModal({
																		...this.props.prizeAddModal,
																					conditions: next,
																	});
																}}
															>
																Remove
															</Button>
														),
													},
												]}
															footer={
																<FormField stretch label="Add condition">
																	<Grid
																		gridDefinition={
																			groupConditions.length
																				? [
																						{ colspan: 2 },
																						{ colspan: 4 },
																						{ colspan: 2 },
																						{ colspan: 2 },
																						{ colspan: 2 },
																				  ]
																				: [
																						{ colspan: 4 },
																						{ colspan: 3 },
																						{ colspan: 3 },
																						{ colspan: 2 },
																				  ]
																		}
																	>
																		{groupConditions.length > 0 && (
															<Select
																				selectedOption={selectedConditionLogicalOperator}
																				placeholder="AND/OR"
																				options={logicalOperatorOptions}
																				onChange={({ detail }) =>
																					this.updateNewCondition(group.groupId, {
																						logicalOperator: detail.selectedOption.value,
																					})
																				}
																			/>
																		)}

																		<Select
																			selectedOption={selectedFieldOption}
																placeholder="Select field"
																			options={fieldOptions}
																			onChange={({ detail }) =>
																				this.updateNewCondition(group.groupId, {
																			field: detail.selectedOption.value,
																			fieldLabel: detail.selectedOption.label,
																			conditionalType: detail.selectedOption.type,
																			specialType: detail.selectedOption.specialType,
																			value: null,
																					operator:
																						detail.selectedOption.type === "select" || detail.selectedOption.type === "multiselect"
																							? "="
																							: null,
																				})
																			}
															/>

															<Select
																			selectedOption={selectedOperatorOption}
																placeholder="Select operator"
																			disabled={newCondition.conditionalType === "select" || newCondition.conditionalType === "multiselect"}
																options={[
																	{ value: "=", label: "=" },
																	{ value: ">", label: ">" },
																	{ value: "<", label: "<" },
																	{ value: ">=", label: ">=" },
																	{ value: "<=", label: "<=" },
																	{ value: "IN", label: "IN" },
																]}
																onChange={({ detail }) =>
																				this.updateNewCondition(group.groupId, {
																			operator: detail.selectedOption.value,
																	})
																}
															/>

																		<Box>
																			{this.renderComponentFromType(
																				group.groupId,
																				newCondition.conditionalType,
																				newCondition.specialType
																			)}
																		</Box>

															<Button
																			disabled={isAddDisabled}
																			onClick={() => this.addCohortCondition(group, groupIndex)}
															>
																+ Add
															</Button>
														</Grid>
													</FormField>
															}
															items={groupConditions}
												empty="No conditions set"
											/>
									</SpaceBetween>
												</Container>
											);
										})}
									</SpaceBetween>
								)
						,
							},
						]} />
					</Form>
				) : (
					<Box textAlign="center">
						<Spinner size="large" />
					</Box>
				)}
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const prizeAddModal = useRecoilValue(prizeAddModalStore);
		const setPrizeAddModal = useSetRecoilState(prizeAddModalStore);
		const game = useRecoilValue(gameStore);

		return (
			<Component
				{...props}
				game={game}
				prizeAddModal={prizeAddModal}
				setPrizeAddModal={setPrizeAddModal}
			/>
		);
	};
}

export default withRecoil(PrizeAddModal);
